import {
  access,
  readFile as fsReadFile,
  writeFile,
  mkdir,
  rm,
} from 'node:fs/promises';
import { dirname, join, relative } from 'node:path';
import { EOL } from 'node:os';
import { CliError, CliErrorCode } from '@wix/cli-error';
import dotenv from 'dotenv';
import { packageJsonSchema } from './schemas.js';
import type { PackageJson } from './schemas.js';

export function toJsonString(object: unknown, opts?: { spaces: number }) {
  return JSON.stringify(object, null, opts?.spaces).concat(EOL);
}

export async function writeJson(
  filePath: string,
  object: unknown,
  opts?: { spaces: number }
) {
  const str = toJsonString(object, opts);
  await outputDir(dirname(filePath));

  try {
    await writeFile(filePath, str, 'utf-8');
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToWriteJson({ filePath }),
      cause: e,
    });
  }
}

export async function readJson(file: string): Promise<unknown> {
  let content = null;
  try {
    content = await fsReadFile(file, 'utf-8');
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToReadJson({ filePath: file }),
      cause: e,
    });
  }

  try {
    return JSON.parse(content);
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.InvalidJsonFile({ filePath: file }),
      cause: e,
    });
  }
}

export async function readFile(file: string): Promise<Buffer>;
export async function readFile(
  file: string,
  encoding: 'utf-8'
): Promise<string>;
export async function readFile(file: string, encoding?: 'utf-8') {
  try {
    return await fsReadFile(file, encoding);
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToReadFile({ filePath: file }),
      cause: e,
    });
  }
}

export function pathExists(path: string) {
  return access(path)
    .then(() => true)
    .catch(() => false);
}

export async function outputFile(file: string, content: string | Uint8Array) {
  await outputDir(dirname(file));

  try {
    await writeFile(file, content, 'utf-8');
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToWriteFile({ filePath: file }),
      cause: e,
    });
  }
}

export async function removeDir(dir: string) {
  if (!(await pathExists(dir))) {
    return;
  }

  try {
    await rm(dir, { recursive: true });
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToRemoveDir({ dirPath: dir }),
      cause: e,
    });
  }
}

export async function outputDir(dir: string) {
  try {
    await mkdir(dir, { recursive: true });
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToCreateDir({ dirPath: dir }),
      cause: e,
    });
  }
}

export async function readPackageJson(projectFolder: string) {
  const packageJsonPath = join(projectFolder, 'package.json');

  const json = await readJson(packageJsonPath);
  const parsedPackageJson = await packageJsonSchema.safeParseAsync(json);

  if (!parsedPackageJson.success) {
    throw new CliError({
      code: CliErrorCode.InvalidConfigSchemaError({
        configPath: packageJsonPath,
        zodError: parsedPackageJson.error,
      }),
      cause: parsedPackageJson.error,
    });
  }

  /**
   * Returning original json because zod clone object and change the order of keys.
   * It's safe to return the original json because we already validated it.
   */
  return json as PackageJson;
}

export function readPackageJsonContent(projectFolder: string) {
  return readFile(join(projectFolder, 'package.json'), 'utf-8');
}

export async function writePackageJson(
  projectFolder: string,
  packageJson: PackageJson
) {
  const packageJsonPath = join(projectFolder, 'package.json');

  await writeJson(packageJsonPath, packageJson, { spaces: 2 });
}

export function relativeFolderName(rootFolder: string, filePath: string) {
  return relative(rootFolder, dirname(filePath));
}

export { type PackageJson } from './schemas.js';

export async function readEnvFile(source: string) {
  if (await pathExists(source)) {
    try {
      const content = await readFile(source);
      return dotenv.parse(content);
    } catch {
      return {};
    }
  }
  return {};
}

export async function updateEnvFile(
  target: string,
  variables: Record<string, string>
) {
  const mergedEnvVars = {
    ...(await readEnvFile(target)),
    ...variables,
  };

  const outputEnvVar = Object.entries(mergedEnvVars)
    .map(([key, value]) => `${key}="${value}"`)
    .join('\n');

  await outputFile(target, outputEnvVar);
}
